import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CaptureDrawer } from "../components/CaptureDrawer";
import { TabBar } from "../components/TabBar";
import { streamSse } from "../lib/sse";
import type { SourceHit } from "../lib/api-types";
import type { Vault } from "../contexts/vault-types";

export default function Answer() {
  const { id } = useParams();
  const [question, setQuestion] = useState<string>("");
  const [sources, setSources] = useState<SourceHit[]>([]);
  const [answer, setAnswer] = useState("");
  const [noResults, setNoResults] = useState(false);
  const [drawer, setDrawer] = useState<{ vault: Vault; slug: string } | null>(null);

  useEffect(() => {
    if (!id) return;
    const pending = sessionStorage.getItem(`pending:${id}`);
    if (!pending) return;
    const p = JSON.parse(pending) as { question: string; vaults: Vault[] };
    setQuestion(p.question);
    let cancelled = false;
    (async () => {
      const res = await fetch("/api/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      for await (const ev of streamSse(res)) {
        if (cancelled) return;
        if (ev.event === "sources") setSources(ev.data.sources);
        else if (ev.event === "delta") setAnswer(a => a + ev.data.text);
        else if (ev.event === "done") {
          if (ev.data.noResults) setNoResults(true);
          sessionStorage.removeItem(`pending:${id}`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const renderAnswer = () => {
    const parts = answer.split(/(\[\d+\])/g);
    return parts.map((p, i) => {
      const m = p.match(/^\[(\d+)\]$/);
      if (m) {
        const n = parseInt(m[1], 10);
        const src = sources[n - 1];
        return (
          <button
            key={i}
            onClick={() => src && setDrawer({ vault: src.vault, slug: src.id })}
            className="inline-block bg-attic-accent-bg text-attic-accent rounded px-1 mx-0.5 text-[10px] font-bold"
          >
            {n}
          </button>
        );
      }
      return <span key={i}>{p}</span>;
    });
  };

  return (
    <div className="min-h-screen pb-20 px-4 pt-3">
      <h1 className="text-attic-accent font-bold text-base mb-3">attic</h1>
      <div className="bg-attic-surface border border-attic-accent rounded-xl px-3 py-2.5 text-sm mb-3">
        ⌕ {question}
      </div>
      {sources.length > 0 && (
        <p className="text-[10px] text-attic-dim mb-3">Searched · {sources.length} sources matched</p>
      )}
      <div className="text-sm leading-relaxed">
        {noResults
          ? "Nothing in your selected vaults matched closely enough. Try a different question or widen the scope."
          : renderAnswer()}
      </div>
      {sources.length > 0 && (
        <div className="mt-4 pt-3 border-t border-attic-border">
          <p className="text-[10px] uppercase font-bold text-attic-dim tracking-wider mb-2">Sources</p>
          {sources.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 py-1">
              <button
                onClick={() => setDrawer({ vault: s.vault, slug: s.id })}
                className="flex-1 text-left flex gap-2 text-xs"
              >
                <span className="bg-attic-accent-bg text-attic-accent px-1 rounded font-bold">{i + 1}</span>
                <span>{s.title} — {s.source}</span>
              </button>
              {s.url && (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-attic-accent text-base font-semibold px-2"
                  aria-label="View original"
                >
                  ↗
                </a>
              )}
            </div>
          ))}
        </div>
      )}
      {drawer && (
        <CaptureDrawer vault={drawer.vault} slug={drawer.slug} onClose={() => setDrawer(null)} />
      )}
      <TabBar />
    </div>
  );
}
